import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  BsArrowLeft,
  BsPencilSquare,
  BsPersonFill,
} from "react-icons/bs";

import Sidebar from "../../components/sidebar/Sidebar";

import { serviceGetEmployeeInfo } from "../../api/serviceApi";
import { getImageUrl } from "../../utils/helper";

import "./viewemployee.css";

// ============================================================
// HELPERS
// ============================================================

const getValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  return String(value);
};

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-AU");
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-AU");
};

// ============================================================
// COMPONENT
// ============================================================

const ViewEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================================================
  // LOAD EMPLOYEE
  // ==========================================================

  useEffect(() => {
    if (!id) {
      return;
    }

    let cancelled = false;

    const loadEmployee = async () => {
      try {
        setLoading(true);

        const result =
          await serviceGetEmployeeInfo(id);

        if (cancelled) {
          return;
        }

        /*
         * Supports:
         *
         * { id, name, ... }
         * { user: {...} }
         * { employee: {...} }
         * { data: {...} }
         */
        const employee =
          result?.user ||
          result?.employee ||
          result?.data ||
          result;

        setInfo(
          employee &&
            typeof employee === "object"
            ? employee
            : null,
        );
      } catch (error) {
        console.error(
          "Error loading employee:",
          error,
        );

        if (!cancelled) {
          setInfo(null);

          toast.error(
            error?.message ||
              "Failed to load employee.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadEmployee();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // ==========================================================
  // BACK
  // ==========================================================

  const goBackHandler = () => {
    navigate(-1);
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div className="view-employee-page">
        <Sidebar />

        <main className="view-employee-content">
          <div className="view-employee-container">
            <div className="view-employee-state">
              <div className="view-employee-state-icon">
                <BsPersonFill />
              </div>

              <strong>
                Loading employee...
              </strong>

              <span>
                Please wait while the employee details are loaded.
              </span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================
  // NOT FOUND
  // ==========================================================

  if (!info) {
    return (
      <div className="view-employee-page">
        <Sidebar />

        <main className="view-employee-content">
          <div className="view-employee-container">
            <div className="view-employee-state">
              <div className="view-employee-state-icon">
                <BsPersonFill />
              </div>

              <strong>
                Employee not found
              </strong>

              <span>
                This employee may have been deleted or is no longer available.
              </span>

              <button
                type="button"
                className="view-employee-btn primary"
                onClick={goBackHandler}
              >
                <BsArrowLeft />
                Back
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ==========================================================
  // NORMALISED DATA
  // ==========================================================

  const employmentType =
    info.employmentType ||
    info.employment_type ||
    "";

  const addedAt =
    info.addedAt ||
    info.added_at ||
    "";

  const addedBy =
    info.addedBy ||
    info.added_by ||
    "";

  const updatedAt =
    info.updatedAt ||
    info.updated_at ||
    "";

  const updatedBy =
    info.updatedBy ||
    info.updated_by ||
    "";

  const imageUrl =
    getImageUrl(info.imageUrl);

  const employeeName =
    info.name ||
    info.username ||
    "Unnamed Employee";

  const employeeSubtitle =
    info.username
      ? `@${info.username}`
      : info.email || "Employee";

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="view-employee-page">
      <Sidebar />

      <main className="view-employee-content">
        <div className="view-employee-container">
          {/* ================================================
              HEADER
          ================================================= */}

          <header className="view-employee-header">
            <div>
              <button
                type="button"
                className="view-employee-back"
                onClick={goBackHandler}
              >
                <BsArrowLeft />
                Back to Employees
              </button>

              <h1>Employee Details</h1>

              <p>
                View account, employment and personal information.
              </p>
            </div>

            <Link
              to={`/employees/update/${id}`}
              className="view-employee-btn primary"
            >
              <BsPencilSquare />
              Edit Employee
            </Link>
          </header>

          {/* ================================================
              MAIN CARD
          ================================================= */}

          <div className="view-employee-card">
            {/* ==============================================
                LEFT SIDEBAR
            =============================================== */}

            <aside className="view-employee-sidebar">
              <div className="view-employee-image">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={employeeName}
                    onError={(event) => {
                      event.currentTarget.style.display =
                        "none";

                      event.currentTarget
                        .closest(
                          ".view-employee-image",
                        )
                        ?.classList.add(
                          "image-error",
                        );
                    }}
                  />
                ) : (
                  <BsPersonFill />
                )}

                <div className="view-employee-image-fallback">
                  <BsPersonFill />
                </div>
              </div>

              <div className="view-employee-summary">
                <strong>
                  {employeeName}
                </strong>

                <span>
                  {employeeSubtitle}
                </span>
              </div>

              <div className="view-employee-role">
                <span>Employee Role</span>

                <strong>
                  {info.role || "—"}
                </strong>
              </div>

              {employmentType && (
                <div className="view-employee-sidebar-meta">
                  <span>
                    Employment Type
                  </span>

                  <strong>
                    {employmentType}
                  </strong>
                </div>
              )}

              {info.email && (
                <div className="view-employee-sidebar-meta">
                  <span>Email</span>

                  <strong>
                    {info.email}
                  </strong>
                </div>
              )}
            </aside>

            {/* ==============================================
                DETAILS
            =============================================== */}

            <div className="view-employee-details">
              {/* ============================================
                  ACCOUNT INFORMATION
              ============================================= */}

              <section className="view-employee-section">
                <div className="view-employee-section-header">
                  <h2>
                    Account Information
                  </h2>

                  <p>
                    Login, role and employment information.
                  </p>
                </div>

                <div className="view-employee-grid">
                  <EmployeeField
                    label="Username"
                    value={info.username}
                  />

                  <EmployeeField
                    label="Email"
                    value={info.email}
                  />

                  <EmployeeField
                    label="Role"
                    value={info.role}
                  />

                  <EmployeeField
                    label="Employment Type"
                    value={employmentType}
                  />
                </div>
              </section>

              <div className="view-employee-divider" />

              {/* ============================================
                  PERSONAL DETAILS
              ============================================= */}

              <section className="view-employee-section">
                <div className="view-employee-section-header">
                  <h2>
                    Personal Details
                  </h2>

                  <p>
                    Employee contact and personal information.
                  </p>
                </div>

                <div className="view-employee-grid">
                  <EmployeeField
                    label="Name"
                    value={info.name}
                  />

                  <EmployeeField
                    label="Phone"
                    value={info.phone}
                  />

                  <EmployeeField
                    label="Date of Birth"
                    value={
                      info.dob
                        ? formatDate(info.dob)
                        : ""
                    }
                  />

                  {info.gender && (
                    <EmployeeField
                      label="Gender"
                      value={info.gender}
                    />
                  )}

                  <EmployeeField
                    label="Address"
                    value={info.address}
                    full
                  />
                </div>
              </section>

              {/* ============================================
                  ACTIVITY
              ============================================= */}

              {(addedAt ||
                addedBy ||
                updatedAt ||
                updatedBy) && (
                <>
                  <div className="view-employee-divider" />

                  <section className="view-employee-section">
                    <div className="view-employee-section-header">
                      <h2>Activity</h2>

                      <p>
                        Creation and modification information.
                      </p>
                    </div>

                    <div className="view-employee-grid">
                      {addedBy && (
                        <EmployeeField
                          label="Added By"
                          value={addedBy}
                        />
                      )}

                      {addedAt && (
                        <EmployeeField
                          label="Added At"
                          value={formatDateTime(
                            addedAt,
                          )}
                        />
                      )}

                      {updatedBy && (
                        <EmployeeField
                          label="Last Updated By"
                          value={updatedBy}
                        />
                      )}

                      {updatedAt && (
                        <EmployeeField
                          label="Last Updated At"
                          value={formatDateTime(
                            updatedAt,
                          )}
                        />
                      )}
                    </div>
                  </section>
                </>
              )}

              {/* ============================================
                  ACTIONS
              ============================================= */}

              <div className="view-employee-actions">
                <button
                  type="button"
                  className="view-employee-btn ghost"
                  onClick={goBackHandler}
                >
                  <BsArrowLeft />
                  Back
                </button>

                <Link
                  to={`/employees/update/${id}`}
                  className="view-employee-btn primary"
                >
                  <BsPencilSquare />
                  Edit Employee
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// ============================================================
// EMPLOYEE FIELD
// ============================================================

const EmployeeField = ({
  label,
  value,
  full = false,
}) => {
  return (
    <div
      className={`view-employee-field ${
        full
          ? "view-employee-full"
          : ""
      }`}
    >
      <span className="view-employee-label">
        {label}
      </span>

      <span>
        {getValue(value)}
      </span>
    </div>
  );
};

export default ViewEmployee;